import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import transportApi from '@/features/dashboard/tourTransport/api/transportApi.ts';
import { transportApiQueryKeys } from '@/features/dashboard/tourTransport/api/transportApi.queryKeys.ts';
import { transportConfigurationMapper } from '@/features/dashboard/tourTransport/types/TransportConfigurationDto.mapper.ts';
import { TransportFormData } from '@/features/dashboard/tourTransport/schemas/transportSchema.ts';
import { toursApiQueryKeys } from '@/features/dashboard/tours/api/toursApi.queryKeys.ts';
import tfgApi from '@/features/dashboard/tfg/api/tfgApi.ts';
import { tfgApiQueryKeys } from '@/features/dashboard/tfg/api/tfgApi.queryKeys.ts';
import { tfgSettingsMapper } from '@/features/dashboard/tfg/types/TfgSettingsDto.mapper.ts';

/**
 * Saves transport config and TFG regulatory settings in one flow (A11).
 * Transport must be saved first so newly-added variants get their backend ids,
 * which the tfg-settings payload references via variantTypings.
 */
export default function usePutTourTransportAndTfg(tourId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TransportFormData) => {
      const saved = await transportApi.put(
        tourId,
        transportConfigurationMapper.fromFormToDto(data),
      );
      await tfgApi.putSettings(
        tourId,
        tfgSettingsMapper.buildPutDto(data, saved.variants),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transportApiQueryKeys.byTourId(tourId),
      });
      queryClient.invalidateQueries({
        queryKey: tfgApiQueryKeys.settings(tourId),
      });
      queryClient.invalidateQueries({
        queryKey: toursApiQueryKeys.activationReadiness(tourId),
      });
      toast.success('Ustawienia transportu zostały zapisane');
    },
    onError: (error: Error) => {
      toast.error('Wystąpił błąd', {
        description:
          error.message || 'Wystąpił błąd podczas zapisywania transportu.',
      });
    },
  });
}
